<template>
  <div class="modal-overlay">
    <div class="modal-content" style="max-width: 75vw">
      <div class="modal-header">
        <div class="header-left">
          <h5 class="modal-title">📊 Tooth 36 - Clinical Summary</h5>
          <p class="modal-subtitle">Lower Left First Molar • Permanent</p>
        </div>
        <button type="button" class="close" @click="closeModal">&times;</button>
      </div>

      <div class="modal-body">
        <!-- Status Overview -->
        <div class="overview-section">
          <div class="status-overview">
            <div class="status-card critical">
              <div class="status-icon">⚠️</div>
              <div class="status-info">
                <h4>Primary Status</h4>
                <p class="status-value">Carious with Defective Restoration</p>
                <p class="status-note">
                  Requires treatment - Recurrent caries detected
                </p>
              </div>
            </div>
            <div class="status-card">
              <div class="status-icon">📅</div>
              <div class="status-info">
                <h4>Last Treatment</h4>
                <p class="status-value">March 10, 2020</p>
                <p class="status-note">MO Composite by Dr. Smith</p>
              </div>
            </div>
            <div class="status-card">
              <div class="status-icon">🩺</div>
              <div class="status-info">
                <h4>Current Symptoms</h4>
                <p class="status-value">Sensitivity to Cold</p>
                <p class="status-note">Patient reported</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Main Content Grid -->
        <div class="content-grid">
          <!-- Left Column -->
          <div class="content-column">
            <!-- Tooth Details -->
            <div class="section-card">
              <div class="section-header">
                <h4><span class="icon">🦷</span> Tooth Details</h4>
              </div>
              <div class="section-body">
                <div class="detail-grid">
                  <div class="detail-item">
                    <span class="detail-label">Tooth Number:</span>
                    <span class="detail-value">36 (FDI)</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Universal:</span>
                    <span class="detail-value">19</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Type:</span>
                    <span class="detail-value">First Molar</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Arch:</span>
                    <span class="detail-value">Mandibular</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Quadrant:</span>
                    <span class="detail-value">Lower Left</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Eruption Status:</span>
                    <span class="detail-value">Fully Erupted</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Surface Status -->
            <div class="section-card">
              <div class="section-header">
                <h4><span class="icon">🔍</span> Surface Status</h4>
              </div>
              <div class="section-body">
                <div class="surface-grid">
                  <div class="surface-item sound">
                    <span class="surface-label">Distal (D)</span>
                    <span class="surface-status">Sound</span>
                  </div>
                  <div class="surface-item sound">
                    <span class="surface-label">Lingual (L)</span>
                    <span class="surface-status">Sound</span>
                  </div>
                  <div class="surface-item caries">
                    <span class="surface-label">Occlusal (O)</span>
                    <span class="surface-status">Caries - Moderate</span>
                  </div>
                  <div class="surface-item restored">
                    <span class="surface-label">Mesial (M)</span>
                    <span class="surface-status">Restored</span>
                  </div>
                  <div class="surface-item sound">
                    <span class="surface-label">Buccal (B)</span>
                    <span class="surface-status">Sound</span>
                  </div>
                </div>
                <div class="surface-notes">
                  <p>
                    <span class="note-label">📝 Note:</span> Deep occlusal
                    caries requiring intervention
                  </p>
                </div>
              </div>
            </div>

            <!-- Endodontic Status -->
            <div class="section-card">
              <div class="section-header">
                <h4><span class="icon">🧪</span> Endodontic Status</h4>
              </div>
              <div class="section-body">
                <div class="status-indicators">
                  <div class="indicator normal">
                    <span class="indicator-icon">○</span>
                    <span class="indicator-text"
                      >Root Canal Treatment: Not Required</span
                    >
                  </div>
                  <div class="indicator normal">
                    <span class="indicator-icon">○</span>
                    <span class="indicator-text">Apical Pathology: Absent</span>
                  </div>
                  <div class="indicator normal">
                    <span class="indicator-icon">○</span>
                    <span class="indicator-text"
                      >Percussion Test: Negative</span
                    >
                  </div>
                  <div class="indicator normal">
                    <span class="indicator-icon">○</span>
                    <span class="indicator-text">Vitality: Vital</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Right Column -->
          <div class="content-column">
            <!-- Restorations -->
            <div class="section-card">
              <div class="section-header">
                <h4><span class="icon">👑</span> Existing Restoration</h4>
              </div>
              <div class="section-body">
                <div class="restoration-display defective">
                  <div class="restoration-header">
                    <div class="restoration-type">
                      <span class="type-icon">🔧</span>
                      <div>
                        <h5>Composite Restoration</h5>
                        <p class="restoration-date">Placed: March 10, 2020</p>
                      </div>
                    </div>
                    <span class="restoration-status defective">Defective</span>
                  </div>
                  <div class="restoration-details">
                    <div class="detail-row">
                      <span class="detail-label">Surfaces:</span>
                      <span class="detail-value">M, O (Mesio-Occlusal)</span>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">Material:</span>
                      <span class="detail-value">Nanofilled Composite</span>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">Provider:</span>
                      <span class="detail-value">Dr. Smith</span>
                    </div>
                    <div class="detail-row">
                      <span class="detail-label">Issue:</span>
                      <span class="detail-value highlight"
                        >Recurrent caries at margin</span
                      >
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Periodontal Status -->
            <div class="section-card">
              <div class="section-header">
                <h4><span class="icon">📊</span> Periodontal Status</h4>
              </div>
              <div class="section-body">
                <div class="measurement-grid">
                  <div class="measurement-item normal">
                    <span class="measurement-label">Pocket Depth</span>
                    <span class="measurement-value">3.0 mm</span>
                    <span class="measurement-status">Normal</span>
                  </div>
                  <div class="measurement-item normal">
                    <span class="measurement-label">Recession</span>
                    <span class="measurement-value">0.5 mm</span>
                    <span class="measurement-status">Normal</span>
                  </div>
                  <div class="measurement-item warning">
                    <span class="measurement-label">Bleeding</span>
                    <span class="measurement-value">Present</span>
                    <span class="measurement-status">⚠️ Monitor</span>
                  </div>
                  <div class="measurement-item normal">
                    <span class="measurement-label">CAL</span>
                    <span class="measurement-value">0 mm</span>
                    <span class="measurement-status">Normal</span>
                  </div>
                </div>

                <div class="plaque-section">
                  <h5><span class="icon">🧫</span> Plaque Index: 1</h5>
                  <div class="plaque-scale">
                    <div
                      class="scale-item"
                      :class="{ active: index === 1 }"
                      v-for="index in 4"
                      :key="index"
                    >
                      <span class="scale-value">{{ index - 1 }}</span>
                      <span class="scale-label">{{
                        getPlaqueLabel(index - 1)
                      }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Mobility & Furcation -->
            <div class="section-card">
              <div class="section-header">
                <h4><span class="icon">⚖️</span> Mobility & Furcation</h4>
              </div>
              <div class="section-body">
                <div class="assessment-display">
                  <div class="mobility-assessment">
                    <h5>Mobility Grading</h5>
                    <div class="grade-display">
                      <div
                        class="grade-item"
                        v-for="grade in [0, 1, 2, 3]"
                        :key="grade"
                      >
                        <span class="grade-label"
                          >Grade {{ grade === 0 ? "0" : grade }}</span
                        >
                        <span
                          class="grade-status"
                          :class="{ active: grade === 0 }"
                        >
                          {{ grade === 0 ? "✓" : "○" }}
                        </span>
                      </div>
                    </div>
                    <p class="assessment-summary">
                      No clinical mobility detected
                    </p>
                  </div>

                  <div class="furcation-assessment">
                    <h5>Furcation Involvement</h5>
                    <div class="grade-display">
                      <div
                        class="grade-item"
                        v-for="grade in ['None', 'I', 'II', 'III']"
                        :key="grade"
                      >
                        <span class="grade-label">{{ grade }}</span>
                        <span
                          class="grade-status"
                          :class="{ active: grade === 'None' }"
                        >
                          {{ grade === "None" ? "✓" : "○" }}
                        </span>
                      </div>
                    </div>
                    <p class="assessment-summary">
                      No furcation involvement present
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Treatment History -->
        <div class="section-card">
          <div class="section-header">
            <h4><span class="icon">🕰️</span> Treatment History</h4>
          </div>
          <div class="section-body">
            <div class="timeline">
              <div class="timeline-item current">
                <div class="timeline-marker current">●</div>
                <div class="timeline-content">
                  <h5>Current Condition</h5>
                  <p>Carious lesion with defective restoration</p>
                  <span class="timeline-date">Present</span>
                </div>
              </div>
              <div class="timeline-item">
                <div class="timeline-marker">●</div>
                <div class="timeline-content">
                  <h5>Composite Restoration</h5>
                  <p>MO composite placed for initial caries</p>
                  <span class="timeline-date">March 10, 2020</span>
                </div>
              </div>
              <div class="timeline-item">
                <div class="timeline-marker">●</div>
                <div class="timeline-content">
                  <h5>Initial Examination</h5>
                  <p>Tooth erupted and documented</p>
                  <span class="timeline-date">2012</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <div class="footer-info">
          <span class="record-info">🆔 Record: DENT-2024-36-001</span>
          <span class="update-info">🔄 Last Updated: Today, 10:42 AM</span>
        </div>
        <div class="footer-actions">
          <button type="button" class="btn btn-secondary" @click="closeModal">
            <span class="btn-icon">←</span> Back to Chart
          </button>
          <button type="button" class="btn btn-primary" @click="printSummary">
            <span class="btn-icon">🖨️</span> Print Summary
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: "ToothChartPresentation",
  methods: {
    closeModal() {
      this.$emit("close-tooth-input");
    },
    printSummary() {
      // Print functionality would be implemented here
      console.log("Print summary");
    },
    getPlaqueLabel(index) {
      const labels = ["No plaque", "Plaque at margin", "Moderate", "Abundant"];
      return labels[index];
    },
  },
};
</script>

<style scoped>
/* Modal Styles */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
}

.modal-content {
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  animation: modalSlideIn 0.3s ease-out;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: 24px 32px;
  background: linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%);
  color: white;
  border-radius: 16px 16px 0 0;
}

.header-left {
  flex: 1;
}

.modal-title {
  margin: 0;
  color:white;
  font-size: 1.8rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 12px;
}

.modal-subtitle {
  margin: 6px 0 0;
  opacity: 0.9;
  font-size: 1.1rem;
}

.close {
  background: rgba(255, 255, 255, 0.15);
  border: none;
  font-size: 2rem;
  color: white;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: background 0.2s;
  margin-left: 20px;
}

.close:hover {
  background: rgba(255, 255, 255, 0.25);
}

.modal-body {
  padding: 24px 32px;
  overflow-y: auto;
  flex-grow: 1;
}

/* Overview Section */
.overview-section {
  margin-bottom: 32px;
}

.status-overview {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-bottom: 24px;
}

@media (max-width: 1200px) {
  .status-overview {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .status-overview {
    grid-template-columns: 1fr;
  }
}

.status-card {
  background: white;
  border: 2px solid #e2e8f0;
  border-radius: 12px;
  padding: 20px;
  display: flex;
  gap: 16px;
  align-items: center;
  transition: transform 0.2s;
}

.status-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08);
}

.status-card.critical {
  border-color: #ef4444;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
}

.status-icon {
  font-size: 2.5rem;
}

.status-info h4 {
  margin: 0 0 8px 0;
  color: #1e293b;
  font-size: 1.1rem;
  font-weight: 600;
}

.status-value {
  margin: 0;
  color: #1e293b;
  font-weight: 700;
  font-size: 1.3rem;
}

.status-note {
  margin: 6px 0 0;
  color: #64748b;
  font-size: 0.95rem;
}

/* Content Grid */
.content-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 24px;
  margin-bottom: 32px;
}

@media (max-width: 1200px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
}

/* Section Cards */
.section-card {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 24px;
}

.section-header {
  padding: 20px 24px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-bottom: 1px solid #e2e8f0;
}

.section-header h4 {
  margin: 0;
  color: #1e293b;
  font-size: 1.2rem;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 10px;
}

.icon {
  font-size: 1.4rem;
}

.section-body {
  padding: 24px;
}

/* Detail Grid */
.detail-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
  border-bottom: 1px solid #f1f5f9;
}

.detail-item:last-child {
  border-bottom: none;
}

.detail-label {
  color: #64748b;
  font-weight: 500;
}

.detail-value {
  color: #1e293b;
  font-weight: 600;
}

/* Surface Grid */
.surface-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.surface-item {
  padding: 16px;
  border-radius: 8px;
  border: 2px solid;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.surface-item.sound {
  border-color: #10b981;
  background: #f0fdf4;
}

.surface-item.caries {
  border-color: #ef4444;
  background: #fef2f2;
}

.surface-item.restored {
  border-color: #3b82f6;
  background: #eff6ff;
}

.surface-label {
  font-weight: 600;
  color: #1e293b;
}

.surface-status {
  font-weight: 600;
  font-size: 0.95rem;
}

.surface-item.sound .surface-status {
  color: #10b981;
}

.surface-item.caries .surface-status {
  color: #ef4444;
}

.surface-item.restored .surface-status {
  color: #3b82f6;
}

.surface-notes {
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  border-left: 4px solid #3b82f6;
}

.note-label {
  font-weight: 600;
  color: #475569;
}

/* Status Indicators */
.status-indicators {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.indicator {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  border-radius: 8px;
}

.indicator.normal {
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.indicator-icon {
  font-size: 1.2rem;
  font-weight: 700;
}

.indicator.normal .indicator-icon {
  color: #10b981;
}

.indicator-text {
  color: #1e293b;
  font-weight: 500;
}

/* Restoration Display */
.restoration-display {
  padding: 20px;
  border-radius: 10px;
  border: 2px solid;
}

.restoration-display.defective {
  border-color: #ef4444;
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
}

.restoration-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.restoration-type {
  display: flex;
  align-items: center;
  gap: 16px;
}

.type-icon {
  font-size: 2rem;
}

.restoration-type h5 {
  margin: 0;
  color: #1e293b;
  font-size: 1.3rem;
}

.restoration-date {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 0.95rem;
}

.restoration-status {
  padding: 8px 20px;
  border-radius: 20px;
  font-weight: 700;
  font-size: 0.95rem;
}

.restoration-status.defective {
  background: #dc2626;
  color: white;
}

.restoration-details {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.detail-row:last-child {
  border-bottom: none;
}

.highlight {
  color: #dc2626;
  font-weight: 700;
}

/* Measurement Grid */
.measurement-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

.measurement-item {
  padding: 16px;
  border-radius: 8px;
  border: 2px solid;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.measurement-item.normal {
  border-color: #10b981;
  background: #f0fdf4;
}

.measurement-item.warning {
  border-color: #f59e0b;
  background: #fffbeb;
}

.measurement-label {
  color: #64748b;
  font-size: 0.95rem;
  margin-bottom: 8px;
}

.measurement-value {
  font-size: 1.8rem;
  font-weight: 800;
  color: #1e293b;
  margin-bottom: 8px;
}

.measurement-status {
  font-size: 0.9rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 12px;
}

.measurement-item.normal .measurement-status {
  background: #10b981;
  color: white;
}

.measurement-item.warning .measurement-status {
  background: #f59e0b;
  color: white;
}

/* Plaque Scale */
.plaque-section {
  margin-top: 24px;
}

.plaque-section h5 {
  margin: 0 0 16px 0;
  color: #1e293b;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 10px;
}

.plaque-scale {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}

.scale-item {
  text-align: center;
  padding: 12px;
  border-radius: 8px;
  background: #f8fafc;
  border: 2px solid #e2e8f0;
  transition: all 0.3s;
}

.scale-item.active {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-color: #3b82f6;
  transform: scale(1.05);
}

.scale-item.active .scale-value,
.scale-item.active .scale-label {
  color: white;
}

.scale-value {
  display: block;
  font-size: 1.5rem;
  font-weight: 800;
  color: #3b82f6;
  margin-bottom: 6px;
}

.scale-label {
  font-size: 0.85rem;
  color: #64748b;
  line-height: 1.2;
}

/* Assessment Display */
.assessment-display {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;
}

@media (max-width: 768px) {
  .assessment-display {
    grid-template-columns: 1fr;
  }
}

.mobility-assessment h5,
.furcation-assessment h5 {
  margin: 0 0 20px 0;
  color: #1e293b;
  font-size: 1.1rem;
}

.grade-display {
  display: flex;
  justify-content: space-between;
  margin-bottom: 16px;
}

.grade-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.grade-label {
  color: #64748b;
  font-size: 0.9rem;
  font-weight: 500;
}

.grade-status {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e2e8f0;
  color: #64748b;
  font-weight: 700;
  font-size: 1.1rem;
}

.grade-status.active {
  background: #10b981;
  color: white;
}

.assessment-summary {
  margin: 16px 0 0;
  color: #10b981;
  font-weight: 600;
  text-align: center;
  padding: 12px;
  background: #f0fdf4;
  border-radius: 8px;
}

/* Timeline */
.timeline {
  position: relative;
  padding-left: 40px;
}

.timeline::before {
  content: "";
  position: absolute;
  left: 20px;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #e2e8f0;
}

.timeline-item {
  position: relative;
  margin-bottom: 24px;
}

.timeline-marker {
  position: absolute;
  left: -40px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  color: #94a3b8;
}

.timeline-marker.current {
  color: #3b82f6;
  font-size: 1.8rem;
}

.timeline-content {
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 20px;
}

.timeline-content h5 {
  margin: 0 0 8px 0;
  color: #1e293b;
  font-size: 1.1rem;
}

.timeline-content p {
  margin: 0 0 8px 0;
  color: #64748b;
}

.timeline-date {
  color: #3b82f6;
  font-weight: 600;
  font-size: 0.95rem;
}

/* Modal Footer */
.modal-footer {
  padding: 20px 32px;
  background: #f8fafc;
  border-top: 1px solid #e2e8f0;
  border-radius: 0 0 16px 16px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.footer-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.record-info,
.update-info {
  color: #64748b;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

.footer-actions {
  display: flex;
  gap: 16px;
}

.btn {
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  border: none;
  display: flex;
  align-items: center;
  gap: 10px;
  transition: all 0.2s;
}

.btn-secondary {
  background: #e2e8f0;
  color: #475569;
}

.btn-secondary:hover {
  background: #cbd5e1;
  transform: translateY(-1px);
}

.btn-primary {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
}

.btn-icon {
  font-size: 1.2rem;
}

/* Scrollbar Styling */
.modal-body::-webkit-scrollbar {
  width: 8px;
}

.modal-body::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

.modal-body::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
